// frontend/src/modules/platform-billing/types.ts

export interface ModulePrice {
  id: string;
  module_key: string;
  currency: string;
  amount_per_month: string;
  /** Per-seat monthly price added by 018_trial_seat_pricing.sql. May be '0.00' for free or fixed-base modules. */
  seat_amount_per_month?: string;
  /**
   * Seats bundled into `amount_per_month` before `seat_amount_per_month`
   * kicks in (prepaid-billing revamp, 2026-07-29). Payroll is the first
   * module to use this (base includes 10 employees). 0 for modules with no
   * allowance (hr, monitoring — every seat bills from the first one).
   */
  included_seats?: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ModulePriceInput {
  module_key: string;
  currency?: string;
  amount_per_month: number;
  seat_amount_per_month?: number;
  is_active?: boolean;
}

export type SubscriptionStatus =
  | 'trial' | 'active' | 'past_due' | 'suspended' | 'cancelled' | 'read_only';

export interface TenantSubscription {
  tenant_id: string;
  /** Joined tenant display name (super-admin list endpoint only). */
  tenant_name?: string | null;
  status: SubscriptionStatus;
  billing_currency: string;
  trial_ends_at: string | null;
  current_period_start: string;
  current_period_end: string;
  grace_period_ends_at: string | null;
  stripe_customer_id: string | null;
  last_payment_attempt_at: string | null;
  suspended_at: string | null;
  cancelled_at: string | null;
  /** Seats configured during trial wizard. Defaults to 1 at signup. */
  seat_count: number;
  /** Snapshot of module keys enabled at signup or last wizard save. */
  enabled_module_keys: string[];
  /** Saved card (migration 033) — set by the self-serve payment-method flow. */
  stripe_payment_method_id: string | null;
  payment_method_brand: string | null;
  payment_method_last4: string | null;
  /**
   * Comp / free account (migration 043). When billing_exempt is true and the
   * comp is active (comp_expires_at null or in the future), the tenant has full
   * access but is never invoiced.
   */
  billing_exempt: boolean;
  comp_reason: string | null;
  comp_expires_at: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Whether a subscription's comp (billing exemption) is currently in force.
 * Mirrors the backend `isCompActive` predicate (comp.ts). comp_expires_at null
 * = never expires.
 */
export function isCompActive(
  sub: Pick<TenantSubscription, 'billing_exempt' | 'comp_expires_at'> | null | undefined,
  now: Date = new Date(),
): boolean {
  if (!sub || sub.billing_exempt !== true) return false;
  if (sub.comp_expires_at == null) return true;
  const exp = new Date(sub.comp_expires_at);
  if (Number.isNaN(exp.getTime())) return true;
  return exp.getTime() > now.getTime();
}

export interface PlatformInvoice {
  id: string;
  tenant_id: string;
  invoice_number: string;
  period_start: string;
  period_end: string;
  currency: string;
  subtotal: string;
  total: string;
  status: 'open' | 'paid' | 'void' | 'uncollectible';
  issued_at: string;
  due_at: string;
  paid_at: string | null;
  stripe_session_id: string | null;
  stripe_payment_id: string | null;
  notification_log_id: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Live metered seat counts for the current subscription period (pricing
 * revamp, 2026-07-25; payroll basis changed to active headcount + prepaid
 * billing, 2026-07-29). Mirrors `GET /api/billing/seat-counts`.
 *   - clock_in: active employees not currently monitored (ALL actives when
 *     the monitoring module is off).
 *   - monitored: active employees running the monitoring agent.
 *   - employees: total active employees, the basis for Payroll's banded
 *     price (base fee includes an allowance, then a per-employee rate for
 *     the rest). No longer tied to who was actually paid on a finalized run.
 */
export interface SeatCounts {
  clock_in: number;
  monitored: number;
  employees: number;
}

export interface BillProjection {
  tenant_id: string;
  period_start: string;
  period_end: string;
  currency: string;
  lines: Array<{ module_key: string; description: string; amount: number }>;
  subtotal: number;
  total: number;
}
